import { useEffect, useRef, useState } from "react";
import AnimatedIntro from "../AnimatedIntro";
import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import { useScroll, useTransform, motion, easeIn, easeInOut, cubicBezier } from "motion/react";
import GuideBall from "../GuideBall";
import Navbar from "../Navbar";

export default function IntroSection({ className }: { className?: string }) {
  const [hideGuideBall, setHideGuideBall] = useState(false);
  const [ballYOffset, setBallYOffset] = useState(0);

  useEffect(() => {
    // Hide the GuideBall after 6 seconds
    setTimeout(() => {
      setHideGuideBall(true);
    }, 6000);
  }, []);

  const containerRef = useRef<HTMLDivElement>(null!);
  const guideBallRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start end", "end end"] });

  // Animate the bg change from transparent to black
  const backgroundColor = useTransform(scrollYProgress, [0.42, 0.7], ["rgba(0, 0, 0, 0)", "rgba(0, 0, 0, 1)"], { ease: easeIn });

  const calculateYOffset = () => {
    // Get a reference to the cloned guide ball and container
    const guideBall = guideBallRef.current;
    const container = containerRef.current;
    if (!guideBall || !container) return 0;

    // Obtain the top offset of the cloned guide ball and height of window
    // This is probably not the best way to do this but I've wrestled with it for way too long now, and this works well enough
    // I couldn't find a reliable way to get the top offset of the actual guide ball without it being affected by the sticky positioning
    // The sticky positioning kept messing with the top offset, no matter which y / offset value I tried using, no matter with getComputedStyle() or getBoundingClientRect()
    // So I just made a hidden clone of the guide ball to measure from instead
    const height = guideBall.offsetHeight || 0;
    const topOffset = parseFloat(window.getComputedStyle(guideBall).top);
    const innerViewHeight = window.innerHeight;

    // Distance from centered position
    const offset = innerViewHeight / 2 - (topOffset || 0) - height / 2;

    // console.log({
    //   y: y.get(),
    //   topOffset,
    //   bottomOffset,
    //   innerViewHeight,
    //   offset,
    //   height,
    //   scrollPosition,
    //   container: {
    //     top: container.getBoundingClientRect().top,
    //     bottom: container.getBoundingClientRect().bottom,
    //     height: container.offsetHeight,
    //   },
    // });

    return offset;
  };

  // Guide ball animations
  const y = useTransform(scrollYProgress, [0.54, 0.92], [0, ballYOffset], { ease: cubicBezier(0.5, 0, 0.75, 0.5) }); // Animate the guide ball moving up to the center of the screen
  const opacity = useTransform(scrollYProgress, [0.92, 1], ["100%", "0%"], { ease: easeInOut }); // Animate the guide ball fading away
  const scale = useTransform(scrollYProgress, [0.8, 1], [1, 0], { ease: cubicBezier(0.5, 0, 0.75, 0.5) }); // Animate the guide ball shrinking

  useEffect(() => {
    setBallYOffset(calculateYOffset());
    window.addEventListener("resize", () => setBallYOffset(calculateYOffset()));
  }, []);

  return (
    <ScrollAnimContext.Provider value={scrollYProgress}>
      <motion.div style={{ height: "280svh", backgroundColor }} ref={containerRef} className='w-full flex flex-col'>
        <div style={{ height: "100svh" }} className='flex flex-col justify-center mx-auto items-center w-2/3 4xl:w-1/2'>
          <Navbar />
          <AnimatedIntro containerRef={containerRef} />
        </div>
        <motion.div className='sticky mt-auto bottom-32' style={{ y, scale, opacity }}>
          <GuideBall delay={0} />
        </motion.div>
        {/* Clone of GuideBall for position calculation */}
        <div ref={guideBallRef} className='absolute bottom-32 opacity-0 pointer-events-none'>
          <GuideBall />
        </div>
      </motion.div>
    </ScrollAnimContext.Provider>
  );
}
